import axios from "axios";
import React, { useState, useRef, useEffect } from "react";
import {
  FiUploadCloud,
  FiSend,
  FiXCircle,
  FiChevronDown,
  FiUpload,
  FiLink,
  FiPlus,
  FiSave,
  FiRefreshCw,
} from "react-icons/fi";
import { MdClose } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ClipLoader } from "react-spinners";
import { getAuth } from "firebase/auth";
import { ref, set, push, get, getDatabase } from "firebase/database";
import { db } from "./firebase/config";
import { useLocation, useNavigate } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { resizeImage, processMultipleImages } from "./utils/imageProcessor";
import {
  POST_LENGTHS,
  POST_STYLES,
  DEFAULT_POST_LENGTH,
  DEFAULT_POST_STYLES,
} from "./constants/postSettings";

const ProductCreateScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const existingProduct = location.state?.product || null;
  // Đảm bảo lấy đúng ID từ sản phẩm hiện có
  const [productId] = useState(existingProduct?.id || null);
  const [productName, setProductName] = useState(existingProduct?.name || "");
  const [productInfo, setProductInfo] = useState(existingProduct?.info || "");
  const [selectedImages, setSelectedImages] = useState(
    existingProduct?.images || []
  );
  const [imageUrl, setImageUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [fanpages, setFanpages] = useState([]);
  const [selectedFanpages, setSelectedFanpages] = useState(
    existingProduct?.fanpages ? existingProduct.fanpages[0] : null
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [previewImage, setPreviewImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [postLength, setPostLength] = useState(DEFAULT_POST_LENGTH);
  const [postStyles, setPostStyles] = useState(DEFAULT_POST_STYLES);
  const [includeContactInfo, setIncludeContactInfo] = useState(false);
  const [useFanpageInfo, setUseFanpageInfo] = useState(false);

  const [isActive, setIsActive] = useState(existingProduct?.isActive ?? true);
  const [isPosted, setIsPosted] = useState(existingProduct?.isPosted ?? false);

  const [postUrl, setPostUrl] = useState("");

  useEffect(() => {
    if (localStorage.getItem("userAccessToken") == null) {
      navigate("/");
      return;
    }

    window.FB.api(
      "/me/accounts",
      "get",
      { access_token: localStorage.getItem("userAccessToken") },
      function (response) {
        if (response && !response.error) {
          setFanpages(response.data);
          // Nếu chưa có fanpage được chọn và có fanpage trong danh sách
          if (!selectedFanpages && response.data.length > 0) {
            setSelectedFanpages(response.data[0]);
          }
        } else {
          console.error(response.error);
        }
      }
    );
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleFanpageSelect = (fanpage) => {
    setSelectedFanpages(fanpage);
    setIsDropdownOpen(false);
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    setIsUploading(true);

    try {
      const resizedFiles = await Promise.all(
        files.map((file) => resizeImage(file))
      );
      setSelectedImages((prevImages) => {
        const newImages = [...prevImages, ...resizedFiles];
        return newImages.slice(0, 9);
      });
    } catch (error) {
      console.error("Lỗi khi xử lý ảnh:", error);
      toast.error("Có lỗi xảy ra khi xử lý ảnh!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer.files);
    setIsUploading(true);

    try {
      const resizedFiles = await Promise.all(
        files.map((file) => resizeImage(file))
      );
      setSelectedImages((prevImages) => {
        const newImages = [...prevImages, ...resizedFiles];
        return newImages.slice(0, 9); // Giới hạn 4 ảnh
      });
    } catch (error) {
      console.error("Lỗi khi xử lý ảnh:", error);
      toast.error("Có lỗi xảy ra khi xử lý ảnh!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleRemoveImage = (index) => {
    setSelectedImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  const handleAddImageUrl = () => {
    if (!imageUrl.trim()) return;

    // Kiểm tra URL hợp lệ
    try {
      new URL(imageUrl);
    } catch (e) {
      toast.error("URL ảnh không hợp lệ!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setSelectedImages((prevImages) => {
      const newImages = [...prevImages, imageUrl];
      return newImages.slice(0, 9); // Giới hạn 4 ảnh
    });
    setImageUrl("");
  };

  const handleSubmit = async () => {
    if (!productName) {
      toast.error("Vui lòng nhập tên sản phẩm!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (!selectedFanpages) {
      toast.error("Vui lòng chọn Fanpage!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsUploading(true);

    const imageUrls = new Set();
    for (const image of selectedImages.filter(Boolean)) {
      if (typeof image === "string") {
        imageUrls.add(image);
        continue;
      }

      const formData = new FormData();
      formData.append("file", image);
      formData.append("upload_preset", "AutoWork");
      formData.append("api_key", "238276161954221");

      try {
        const response = await axios.post(
          "https://api.cloudinary.com/v1_1/test-upload-1/image/upload",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        imageUrls.add(response.data.secure_url);
      } catch (error) {
        console.error("Failed to upload image:", error);
        toast.error("Failed to upload image!", {
          position: "top-right",
          autoClose: 3000,
        });
        setIsUploading(false);
        return;
      }
    }

    const userId = localStorage.getItem("userId");

    try {
      let productRef;
      let productData;

      const position =
        existingProduct?.position !== undefined
          ? existingProduct.position
          : 999999999;

      if (existingProduct) {
        // Cập nhật sản phẩm hiện có
        productRef = ref(db, `products/${userId}/${existingProduct.id}`);
        productData = {
          id: existingProduct.id,
          name: productName,
          info: productInfo,
          images: Array.from(imageUrls),
          fanpages: [selectedFanpages],
          isActive: isActive,
          isPosted: isPosted,
          position: position, // Ensure position is preserved
        };
      } else {
        // Tạo sản phẩm mới
        const newProductRef = push(ref(db, `products/${userId}`));
        productRef = newProductRef;
        productData = {
          id: newProductRef.key,
          name: productName,
          info: productInfo,
          images: Array.from(imageUrls),
          fanpages: [selectedFanpages],
          isActive: isActive,
          isPosted: false,
          position: position,
        };
      }

      await set(productRef, productData);

      toast.success(
        existingProduct
          ? "Sản phẩm đã được cập nhật thành công!"
          : "Sản phẩm đã được thêm thành công!",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );

      if (!existingProduct) {
        setProductName("");
        setProductInfo("");
        setSelectedImages([]);
        setSelectedFanpages(null);
      }
    } catch (error) {
      console.error("Failed to save product:", error);
      toast.error("Failed to save product!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCreatePost = async () => {
    if (selectedImages.length === 0) {
      toast.error("Vui lòng chọn ít nhất một ảnh để tạo bài viết!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const processedImages = await processMultipleImages(selectedImages);

    navigate("/photos-post", {
      state: {
        images: processedImages,
        productInfo: `Tên sản phẩm: ${productName}\n${productInfo}`,
      },
    });
  };

  const openImagePreview = (imageUrl) => {
    const index = selectedImages.findIndex((img) => {
      if (typeof img === "string") {
        return img === imageUrl;
      }
      return URL.createObjectURL(img) === imageUrl;
    });
    setCurrentImageIndex(index);
    setPreviewImage(imageUrl);
  };

  const closeImagePreview = () => {
    setPreviewImage(null);
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    const newIndex =
      (currentImageIndex - 1 + selectedImages.length) % selectedImages.length;
    setCurrentImageIndex(newIndex);
    const newImage = selectedImages[newIndex];
    setPreviewImage(
      typeof newImage === "string" ? newImage : URL.createObjectURL(newImage)
    );
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    const newIndex = (currentImageIndex + 1) % selectedImages.length;
    setCurrentImageIndex(newIndex);
    const newImage = selectedImages[newIndex];
    setPreviewImage(
      typeof newImage === "string" ? newImage : URL.createObjectURL(newImage)
    );
  };

  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const db = getDatabase();
        const settingsRef = ref(
          db,
          `users/${userId}/settings/productCreateScreen`
        );
        const snapshot = await get(settingsRef);

        if (snapshot.exists()) {
          const settings = snapshot.val();
          setPostLength(settings.postLength || DEFAULT_POST_LENGTH);
          setPostStyles(settings.postStyles || DEFAULT_POST_STYLES);
          setIncludeContactInfo(settings.includeContactInfo || false);
          setUseFanpageInfo(settings.useFanpageInfo || false);
        }
      } catch (error) {
        console.error("Error loading settings:", error);
      }
    };

    loadUserSettings();
  }, []);

  const saveUserSettings = async () => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      const db = getDatabase();
      const settingsRef = ref(
        db,
        `users/${userId}/settings/productCreateScreen`
      );

      const settings = {
        postLength,
        postStyles,
        includeContactInfo,
        useFanpageInfo,
      };

      await set(settingsRef, settings);
    } catch (error) {
      console.error("Error saving settings:", error);
    }
  };

  const handlePostStyleChange = (style) => {
    const newStyles = postStyles.includes(style)
      ? postStyles.filter((s) => s !== style)
      : [...postStyles, style];
    setPostStyles(newStyles);
    saveUserSettings();
  };

  const handleIncludeContactInfoChange = (e) => {
    setIncludeContactInfo(e.target.checked);
    saveUserSettings();
  };

  const handleGetImagesFromPost = async () => {
    if (!postUrl || !selectedFanpages) {
      toast.error("Vui lòng nhập URL bài post và chọn Fanpage!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      // Lấy post ID từ URL
      let postId;
      let urlParams;

      if (postUrl.includes("permalink.php") || postUrl.includes("story.php")) {
        try {
          // Xử lý cả 2 loại URL format
          const url = new URL(postUrl);
          urlParams = new URLSearchParams(url.search);
          const storyFbid = urlParams.get("story_fbid");
          const pageId = urlParams.get("id");

          if (!storyFbid || !pageId) {
            throw new Error("URL không hợp lệ");
          }
          postId = storyFbid;
        } catch (error) {
          throw new Error("URL không hợp lệ");
        }
      } else {
        // Format cũ: .../posts/{postId}
        const postIdMatch = postUrl.match(/\/posts\/(\d+)/);
        if (!postIdMatch) {
          throw new Error("URL không hợp lệ");
        }
        postId = postIdMatch[1];
      }

      const pageId = selectedFanpages.id;
      const pageAccessToken = selectedFanpages.access_token;

      const response = await fetch(
        `https://graph.facebook.com/v21.0/${pageId}_${postId}?fields=id,message,attachments{subattachments{media}},created_time,permalink_url,full_picture&access_token=${pageAccessToken}`
      );

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error.message);
      }

      // Lấy danh sách URL ảnh từ response
      let images = [];

      // Kiểm tra attachments trước
      if (data.attachments?.data[0]?.subattachments?.data) {
        images = data.attachments.data[0].subattachments.data.map(
          (item) => item.media.image.src
        );
      }

      // Nếu không có ảnh trong attachments, kiểm tra full_picture
      if (images.length === 0 && data.full_picture) {
        images = [data.full_picture];
      }

      if (images.length === 0) {
        toast.warning("Không tìm thấy ảnh trong bài post!", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // Thêm các ảnh vào danh sách đã chọn
      setSelectedImages((prevImages) => {
        const newImages = [...prevImages, ...images];
        return newImages.slice(0, 9); // Giới hạn 4 ảnh
      });

      setPostUrl("");
      toast.success("Đã thêm ảnh từ bài post thành công!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error fetching post images:", error);
      toast.error("Không thể lấy ảnh từ bài post: " + error.message, {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleDeleteProduct = () => {
    confirmAlert({
      title: "Xác nhận xóa",
      message: "Bạn có chắc chắn muốn xóa sản phẩm này?",
      buttons: [
        {
          label: "Có",
          onClick: async () => {
            setIsUploading(true);
            const userId = localStorage.getItem("userId");

            try {
              await set(
                ref(db, `products/${userId}/${existingProduct.id}`),
                null
              );

              toast.success("Đã xóa sản phẩm thành công!", {
                position: "top-right",
                autoClose: 3000,
              });

              navigate("/products");
            } catch (error) {
              console.error("Failed to delete product:", error);
              toast.error("Không thể xóa sản phẩm!", {
                position: "top-right",
                autoClose: 3000,
              });
            } finally {
              setIsUploading(false);
            }
          },
        },
        {
          label: "Không",
          onClick: () => {},
        },
      ],
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 pt-5">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6 md:mb-8 text-center">
          {existingProduct ? "Chỉnh Sửa Sản Phẩm" : "Thêm Sản Phẩm"}
        </h1>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Phần trái - Form nhập thông tin */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-lg shadow-md p-5 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Thông tin sản phẩm
                </h2>
                <a
                  href="https://www.youtube.com/watch?v=dHgoo9YANEY&t=1s&ab_channel=AUTOWORK"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium rounded-md shadow-sm transition duration-300 hover:text-red-600"
                  title="Xem video hướng dẫn cách sử dụng"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-red-600"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                  </svg>
                  <span>Hướng dẫn</span>
                </a>
              </div>

              {/* Khu vực kéo thả ảnh */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2 flex items-center gap-2">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="inline-block mb-1"
                  >
                    <path
                      d="M4 16L8.586 11.414C8.96106 11.0391 9.46967 10.8284 10 10.8284C10.5303 10.8284 11.0389 11.0391 11.414 11.414L16 16M14 14L15.586 12.414C15.9611 12.0391 16.4697 11.8284 17 11.8284C17.5303 11.8284 18.0389 12.0391 18.414 12.414L20 14M14 8H14.01M6 20H18C18.5304 20 19.0391 19.7893 19.4142 19.4142C19.7893 19.0391 20 18.5304 20 18V6C20 5.46957 19.7893 4.96086 19.4142 4.58579C19.0391 4.21071 18.5304 4 18 4H6C5.46957 4 4.96086 4.21071 4.58579 4.58579C4.21071 4.96086 4 5.46957 4 6V18C4 18.5304 4.21071 19.0391 4.58579 19.4142C4.96086 19.7893 5.46957 20 6 20Z"
                      stroke="#4B5563"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="inline-block ml-1">Tải ảnh sản phẩm</span>
                </label>

                <div
                  className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
                    ${
                      isUploading
                        ? "border-blue-300 bg-blue-50"
                        : "border-gray-300 hover:border-blue-400 hover:bg-blue-50/30"
                    }
                    ${selectedImages.length > 0 ? "pb-24" : ""}
                  `}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={handleUploadClick}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    onClick={(e) => e.stopPropagation()}
                  />

                  {selectedImages.length === 0 ? (
                    <>
                      <div className="mx-auto w-20 h-20 mb-4 flex items-center justify-center rounded-full bg-blue-50">
                        <FiUpload className="text-3xl text-blue-500" />
                      </div>
                      <p className="text-base text-gray-600 font-medium">
                        Kéo thả ảnh vào đây hoặc
                      </p>
                      <button
                        type="button"
                        className="mt-2 inline-flex items-center text-blue-600 font-medium hover:text-blue-700"
                      >
                        Chọn ảnh từ máy tính
                      </button>
                      <p className="mt-2 text-sm text-gray-500">
                        Định dạng: JPG, PNG, JPEG (tối đa 5MB/ảnh)
                      </p>
                    </>
                  ) : (
                    <div className="py-2">
                      {/* Hiển thị hướng dẫn thêm ảnh */}
                      <div className="flex flex-col sm:flex-row gap-3 justify-center mb-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                        <div className="flex-1 flex flex-col justify-center items-center sm:items-start">
                          <p className="font-medium text-gray-700 mb-1 text-center sm:text-left">
                            Thêm ảnh mới
                          </p>
                          <p className="text-xs text-gray-500 text-center sm:text-left">
                            Nhấn nút hoặc kéo thả vào khu vực này
                          </p>
                        </div>
                        <div className="flex flex-row flex-wrap justify-center gap-2">
                          <button
                            type="button"
                            className="px-3 py-2 bg-white border border-gray-300 rounded text-gray-700 flex items-center gap-1 hover:bg-gray-50 text-sm"
                          >
                            <FiUpload className="text-gray-500" />
                            <span>Từ máy tính</span>
                          </button>
                          <button
                            type="button"
                            className="px-3 py-2 bg-white border border-gray-300 rounded text-gray-700 flex items-center gap-1 hover:bg-gray-50 text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              document
                                .getElementById("image-url-input")
                                .focus();
                            }}
                          >
                            <FiLink className="text-gray-500" />
                            <span>Từ URL</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Hiển thị ảnh đã tải lên */}
              {selectedImages.length > 0 && (
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-gray-700 font-medium">
                      Danh sách ảnh ({selectedImages.length})
                    </label>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                    {selectedImages.map((image, index) => (
                      <div key={index} className="relative aspect-square">
                        <img
                          src={
                            typeof image === "string"
                              ? image
                              : URL.createObjectURL(image)
                          }
                          alt={`Selected ${index}`}
                          className="w-full h-full object-contain bg-gray-100 rounded-lg"
                        />
                        <FiXCircle
                          onClick={() => handleRemoveImage(index)}
                          className="absolute top-2 right-2 text-red-600 cursor-pointer z-10"
                          size={20}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openImagePreview(
                              typeof image === "string"
                                ? image
                                : URL.createObjectURL(image)
                            );
                          }}
                          className="absolute top-2 left-2 bg-gray-800 bg-opacity-50 hover:bg-opacity-70 text-white p-1 rounded-full z-10"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path
                              fillRule="evenodd"
                              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}

                    {selectedImages.length < 4 && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current.click();
                        }}
                        className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30"
                      >
                        <FiPlus className="text-2xl text-gray-400" />
                        <span className="text-sm text-gray-500 mt-1">
                          Thêm ảnh
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Thêm ảnh từ URL */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.1901 8.7127L15.4701 10.9927M8.5601 13.3327L11.1901 15.9627M7.9201 8.2127H7.9301M18.0001 19.0027C19.6569 19.0027 21.0001 17.6596 21.0001 16.0027C21.0001 14.3459 19.6569 13.0027 18.0001 13.0027C16.3432 13.0027 15.0001 14.3459 15.0001 16.0027C15.0001 17.6596 16.3432 19.0027 18.0001 19.0027ZM6.0001 19.0027C7.65685 19.0027 9.0001 17.6596 9.0001 16.0027C9.0001 14.3459 7.65685 13.0027 6.0001 13.0027C4.34335 13.0027 3.0001 14.3459 3.0001 16.0027C3.0001 17.6596 4.34335 19.0027 6.0001 19.0027ZM12.0001 11.0027C13.6569 11.0027 15.0001 9.6596 15.0001 8.0027C15.0001 6.34585 13.6569 5.0027 12.0001 5.0027C10.3432 5.0027 9.0001 6.34585 9.0001 8.0027C9.0001 9.6596 10.3432 11.0027 12.0001 11.0027Z"
                      stroke="#4B5563"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <label
                    className="text-gray-700 font-medium"
                    id="image-url-label"
                  >
                    Thêm ảnh từ URL
                  </label>
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiLink className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="image-url-input"
                    type="text"
                    className="w-full pl-10 pr-28 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    aria-labelledby="image-url-label"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleAddImageUrl();
                      }}
                      disabled={!imageUrl.trim()}
                      className={`h-full px-4 rounded-r-md text-white font-medium transition-colors ${
                        !imageUrl.trim()
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-500 hover:bg-blue-600"
                      }`}
                    >
                      Thêm
                    </button>
                  </div>
                </div>
                <p className="mt-1.5 text-xs text-gray-500">
                  Nhập liên kết trực tiếp đến ảnh (URL phải kết thúc bằng .jpg,
                  .jpeg, .png)
                </p>
              </div>

              {/* Thêm ảnh từ bài post Facebook */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 9.68586V12.9999M12 16.3139H12.01M6.4142 20.0001H17.5858C17.851 20.0001 18.1054 19.8947 18.2929 19.7072L19.7071 18.293C19.8946 18.1055 20 17.851 20 17.5859V6.41414C20 6.149 19.8946 5.89464 19.7071 5.70711L18.2929 4.29289C18.1054 4.10536 17.851 4 17.5858 4H6.4142C6.149 4 5.89464 4.10536 5.70711 4.29289L4.29289 5.70711C4.10536 5.89464 4 6.149 4 6.41414V17.5859C4 17.851 4.10536 18.1055 4.29289 18.293L5.70711 19.7072C5.89464 19.8947 6.14899 20.0001 6.4142 20.0001Z"
                      stroke="#4B5563"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <label className="text-gray-700 font-medium">
                    Thêm ảnh từ bài post Facebook
                  </label>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="#1877F2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96C15.9164 21.5878 18.0622 20.3855 19.6099 18.57C21.1576 16.7546 22.0054 14.4456 22 12.06C22 6.53 17.5 2.04 12 2.04Z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    className="w-full pl-10 pr-28 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    value={postUrl}
                    onChange={(e) => setPostUrl(e.target.value)}
                    placeholder="https://www.facebook.com/..."
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    <button
                      onClick={handleGetImagesFromPost}
                      disabled={!postUrl.trim() || !selectedFanpages}
                      className={`h-full px-4 rounded-r-md text-white font-medium transition-colors ${
                        !postUrl.trim() || !selectedFanpages
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-500 hover:bg-blue-600"
                      }`}
                    >
                      Lấy ảnh
                    </button>
                  </div>
                </div>
                <p className="mt-1.5 text-xs text-gray-500">
                  Nhập URL bài post để lấy ảnh (cần chọn Fanpage trước)
                </p>
              </div>
            </div>
          </div>

          {/* Phần phải - Cấu hình đăng bài */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-lg shadow-md p-5 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Cấu hình đăng bài
              </h2>

              {/* Chọn Fanpage */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Chọn Fanpage để đăng bài
                </label>
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={toggleDropdown}
                    type="button"
                    className="w-full flex justify-between items-center px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50"
                  >
                    <span>
                      {selectedFanpages
                        ? selectedFanpages.name
                        : "Chọn Fanpage"}
                    </span>
                    <FiChevronDown className="w-5 h-5 text-gray-400" />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      {fanpages.map((fanpage) => (
                        <div
                          key={fanpage.id}
                          onClick={() => handleFanpageSelect(fanpage)}
                          className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                            selectedFanpages?.id === fanpage.id
                              ? "bg-blue-50 text-blue-600"
                              : ""
                          }`}
                        >
                          {fanpage.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Tên sản phẩm */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Tên sản phẩm
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="Nhập tên sản phẩm..."
                />
              </div>

              {/* Thông tin sản phẩm */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Thông tin sản phẩm
                </label>
                <textarea
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="4"
                  value={productInfo}
                  onChange={(e) => setProductInfo(e.target.value)}
                  placeholder="Nhập thông tin sản phẩm..."
                ></textarea>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <label className="block text-gray-700 font-medium">
                    Tự động viết và đăng bài
                  </label>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      name="autoPost"
                      id="autoPost"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="hidden"
                    />
                    <label
                      htmlFor="autoPost"
                      className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out
                        ${isActive ? "bg-blue-500" : "bg-gray-300"}`}
                    >
                      <span
                        className={`block h-6 w-6 rounded-full shadow transform transition-transform duration-200 ease-in-out
                          ${
                            isActive
                              ? "translate-x-6 bg-white border-2 border-blue-500"
                              : "translate-x-0 bg-white"
                          }`}
                      />
                    </label>
                  </div>
                </div>
                {isActive ? (
                  <p className="mt-2 text-sm text-gray-600">
                    Hệ thống sẽ tự động viết và đăng bài theo thời gian đã cài
                    đặt
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-gray-600">
                    Hiện tại tính năng tự động viết và đăng bài tự động đã tắt.
                    Vui lòng bật để sử dụng
                  </p>
                )}
              </div>

              <div className="flex flex-row gap-3 mt-6">
                <button
                  onClick={handleSubmit}
                  disabled={isUploading}
                  className="flex-1 flex items-center justify-center bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                  title="Lưu thông tin sản phẩm hiện tại"
                >
                  {isUploading ? (
                    <ClipLoader size={20} color={"#fff"} className="mr-2" />
                  ) : (
                    <FiSave className="mr-2" />
                  )}
                  {isUploading
                    ? "Đang lưu..."
                    : existingProduct
                    ? "Lưu thay đổi"
                    : "Thêm sản phẩm"}
                </button>

                {existingProduct && (
                  <>
                    <button
                      onClick={handleCreatePost}
                      disabled={isUploading}
                      className="flex-1 flex items-center justify-center bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                      title="Tạo bài viết mới từ thông tin sản phẩm này"
                    >
                      <FiSend className="mr-2" />
                      Tạo bài viết mới
                    </button>

                    <button
                      onClick={handleDeleteProduct}
                      disabled={isUploading}
                      className="flex-1 flex items-center justify-center border border-red-600 text-red-600 py-2 px-4 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                      title="Xóa sản phẩm khỏi hệ thống"
                    >
                      {isUploading ? (
                        <ClipLoader
                          size={20}
                          color={"#f87171"}
                          className="mr-2"
                        />
                      ) : (
                        <FiXCircle className="mr-2" />
                      )}
                      Xóa sản phẩm
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
          onClick={closeImagePreview}
        >
          <div className="relative max-w-4xl w-full h-full flex items-center justify-center">
            {/* Previous button */}
            <button
              onClick={handlePrevImage}
              className="absolute left-6 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full z-20"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Close button */}
            <button
              onClick={closeImagePreview}
              className="absolute top-6 right-6 text-white hover:text-gray-300 z-20"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Image */}
            <img
              src={previewImage}
              alt="Preview"
              className="max-h-[90vh] max-w-full w-auto h-auto object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Next button */}
            <button
              onClick={handleNextImage}
              className="absolute right-6 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full z-20"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Image counter */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white bg-black bg-opacity-50 px-3 py-1 rounded-full z-20">
              {currentImageIndex + 1} / {selectedImages.length}
            </div>
          </div>
        </div>
      )}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={true}
        pauseOnHover={true}
        draggable={true}
      />
    </div>
  );
};

export default ProductCreateScreen;
